import { UseFormReturnType } from "@mantine/form";
import { notifications } from "@mantine/notifications";

// ============================================
// SCROLL TO ERROR UTILITIES
// ============================================

export function scrollToFirstError<T>(
  form: UseFormReturnType<T>,
  customErrors?: Record<string, any>
) {
  const errors = customErrors || form.errors;
  const firstErrorField = Object.keys(errors).find((key) => errors[key]);

  if (firstErrorField) {
    // Try specific selectors first, then fall back to aria-invalid
    // which Mantine sets on any input with an error
    const element =
      document.querySelector(`[name="${firstErrorField}"]`) ||
      document.querySelector(`#${firstErrorField}`) ||
      document.querySelector(`[data-path="${firstErrorField}"]`) ||
      document.querySelector('[aria-invalid="true"]');
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });

      // Look for actual focusable element inside (e.g. for RichTextEditor/TipTap)
      const focusable =
        element.querySelector(
          'input:not([type="hidden"]), textarea, select, [contenteditable="true"]'
        ) || element;

      (focusable as HTMLElement).focus?.();
      return true;
    }
  }
  return false;
}

export function showValidationNotification(message: string) {
  notifications.show({
    title: "Validation Error",
    message,
    color: "red",
  });
}

// ============================================
// FIELD VALIDATORS
// ============================================

export const required =
  (message = "This field is required") =>
  (value: string | null | undefined) =>
    !value?.trim() ? message : null;

export const email =
  (message = "Invalid email address") =>
  (value: string | null | undefined) => {
    if (!value || !value.trim()) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value) ? null : message;
  };

export const phone =
  (message = "Invalid phone number") =>
  (value: string | null | undefined) => {
    if (!value) return null;
    // Remove common formatting characters
    const digits = value.replace(/[\s\-().+]/g, "");
    // Should be 10-15 digits
    return /^\d{10,15}$/.test(digits) ? null : message;
  };

export const fax = (message = "Invalid fax number") => phone(message);

export const npi =
  (message = "NPI must be exactly 10 digits") =>
  (value: string | null | undefined) => {
    if (!value) return null;
    return /^\d{10}$/.test(value) ? null : message;
  };

export const integer =
  (message = "Must be a whole number") =>
  (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === "") return null;
    return Number.isInteger(Number(value)) ? null : message;
  };

export const decimal =
  (message = "Must be a valid number") =>
  (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === "") return null;
    return !isNaN(Number(value)) ? null : message;
  };

export const min =
  (minValue: number, message?: string) =>
  (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(value);
    return num >= minValue ? null : message || `Must be at least ${minValue}`;
  };

export const max =
  (maxValue: number, message?: string) =>
  (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(value);
    return num <= maxValue ? null : message || `Must be at most ${maxValue}`;
  };

export const minLength =
  (length: number, message?: string) => (value: string | null | undefined) => {
    if (!value) return null;
    return value.length >= length
      ? null
      : message || `Must be at least ${length} characters`;
  };

export const maxLength =
  (length: number, message?: string) => (value: string | null | undefined) => {
    if (!value) return null;
    return value.length <= length
      ? null
      : message || `Must be at most ${length} characters`;
  };

export const url =
  (message = "Invalid URL") =>
  (value: string | null | undefined) => {
    if (!value) return null;
    try {
      // eslint-disable-next-line no-new
      new URL(value);
      return null;
    } catch {
      return message;
    }
  };

export const zipCode =
  (message = "Invalid zip code") =>
  (value: string | null | undefined) => {
    if (!value) return null;
    return /^\d{5}(-\d{4})?$/.test(value) ? null : message;
  };

// ============================================
// COMPOSE VALIDATORS
// ============================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const compose =
  (...validators: ((value: any) => string | null)[]) =>
  (value: unknown) =>
    validators.reduce<string | null>(
      (error, validator) => error || validator(value),
      null
    );
